/**
 * useScrollProgress Hook
 * ======================
 * Custom hook for tracking scroll progress.
 * Useful for scroll progress indicators and scroll-based animations.
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { throttle } from '../utils/helpers';

/**
 * Hook to track overall page scroll progress
 * @param {object} options - Configuration options
 * @param {number} options.throttleMs - Throttle delay in milliseconds
 * @returns {number} Scroll progress (0 to 1)
 */
export function useScrollProgress(options = {}) {
  const { throttleMs = 16 } = options; // ~60fps
  const [progress, setProgress] = useState(0);
  
  useEffect(() => {
    const handleScroll = throttle(() => {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const scrollHeight = document.documentElement.scrollHeight - window.innerHeight;
      const newProgress = scrollHeight > 0 ? scrollTop / scrollHeight : 0;
      setProgress(Math.min(1, Math.max(0, newProgress)));
    }, throttleMs);
    
    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll(); // Initial calculation
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [throttleMs]);
  
  return progress;
}

/**
 * Hook to track scroll progress of a specific element
 * @param {object} options - Configuration options
 * @param {number} options.start - Start offset (0-1)
 * @param {number} options.end - End offset (0-1)
 * @returns {object} Ref and progress
 */
export function useElementScrollProgress(options = {}) {
  const { start = 0, end = 1 } = options;
  const elementRef = useRef(null);
  const [progress, setProgress] = useState(0);
  const [isInView, setIsInView] = useState(false);
  
  useEffect(() => {
    const element = elementRef.current;
    if (!element) return;
    
    const handleScroll = throttle(() => {
      const rect = element.getBoundingClientRect();
      const windowHeight = window.innerHeight;
      
      // Calculate element's position relative to viewport
      const elementTop = rect.top;
      const elementHeight = rect.height;
      
      // Calculate when element enters and leaves viewport
      const enterPoint = windowHeight * (1 - start);
      const leavePoint = -elementHeight * end;
      const totalDistance = enterPoint - leavePoint;
      
      // Calculate progress
      const currentPosition = enterPoint - elementTop;
      const newProgress = Math.min(1, Math.max(0, currentPosition / totalDistance));
      
      setProgress(newProgress);
      setIsInView(elementTop < windowHeight && rect.bottom > 0);
    }, 16);
    
    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll(); // Initial calculation
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [start, end]);
  
  return { ref: elementRef, progress, isInView };
}

/**
 * Hook to detect when an element enters/exits viewport
 * @param {object} options - IntersectionObserver options
 * @returns {object} Ref and visibility state
 */
export function useInView(options = {}) {
  const { threshold = 0, rootMargin = '0px', triggerOnce = false } = options;
  const elementRef = useRef(null);
  const [isInView, setIsInView] = useState(false);
  const [hasTriggered, setHasTriggered] = useState(false);
  
  useEffect(() => {
    const element = elementRef.current;
    if (!element) return;
    
    // Skip if triggerOnce and already triggered
    if (triggerOnce && hasTriggered) return;
    
    const observer = new IntersectionObserver(
      ([entry]) => {
        const inView = entry.isIntersecting;
        setIsInView(inView);
        
        if (inView && triggerOnce) {
          setHasTriggered(true);
          observer.unobserve(element);
        }
      },
      { threshold, rootMargin }
    );
    
    observer.observe(element);
    
    return () => {
      observer.disconnect();
    };
  }, [threshold, rootMargin, triggerOnce, hasTriggered]);
  
  return { ref: elementRef, isInView };
}

/**
 * Hook to track scroll direction
 * @returns {object} Scroll direction state
 */
export function useScrollDirection() {
  const [scrollDirection, setScrollDirection] = useState('none');
  const [scrollY, setScrollY] = useState(0);
  const lastScrollY = useRef(0);
  
  useEffect(() => {
    const handleScroll = throttle(() => {
      const currentScrollY = window.pageYOffset || document.documentElement.scrollTop;
      
      if (currentScrollY > lastScrollY.current) {
        setScrollDirection('down');
      } else if (currentScrollY < lastScrollY.current) {
        setScrollDirection('up');
      }
      
      lastScrollY.current = currentScrollY;
      setScrollY(currentScrollY);
    }, 100);
    
    window.addEventListener('scroll', handleScroll, { passive: true });
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);
  
  return { scrollDirection, scrollY };
}

/**
 * Hook to lock body scroll
 * @param {boolean} locked - Whether to lock scroll
 */
export function useLockBodyScroll(locked) {
  useEffect(() => {
    const originalStyle = window.getComputedStyle(document.body).overflow;
    
    if (locked) {
      document.body.style.overflow = 'hidden';
    }
    
    return () => {
      document.body.style.overflow = originalStyle;
    };
  }, [locked]);
}

export default useScrollProgress;
